import Box from '@mui/material/Box';
import { setComponents, StoryblokComponent, useStoryblokBridge } from '@storyblok/react';
import React from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import BottomNavigationBar from '../../components/Layouts/BottomNavigationBar/BottomNavigationBar';
import Footer from '../../components/Layouts/Footer/Footer';
import LayoutSingleColumn from '../../components/Layouts/LayoutSingleColumn';
import LayoutWrapperBottombar from '../../components/Layouts/LayoutWrapperBottombar/LayoutWrapperBottombar';
import LayoutWrapperFooter from '../../components/Layouts/LayoutWrapperFooter/LayoutWrapperFooter';
import LayoutWrapperMain from '../../components/Layouts/LayoutWrapperMain';
import LayoutWrapperTopbar from '../../components/Layouts/LayoutWrapperTopbar/LayoutWrapperTopbar';
import TopBar from '../../components/Layouts/TopBar/TopBar';
import NamedRedirect from '../../components/NamedRedirect/NamedRedirect';
import Page from '../../components/Page/Page';
import StoryblokComponents from '../../components/Storyblok';
import config from '../../config';
import useAppSelector from '../../util/hooks/useAppSelector';
import useAppTheme from '../../util/hooks/useAppTheme';
import { localizedAlternateLinks } from '../../util/seo';
import StoryblokErrorPage from './StoryblokErrorPage';
import StoryblokLoadingPage from './StoryblokLoadingPage';
import { bridgeUpdateStoryblokStory } from './StoryblokPage.duck';

const StoryblokPage = () => {
  setComponents(StoryblokComponents);

  const { currentStory, fetchingInProgress, error } = useAppSelector(
    (state) => ({
      currentStory: state.StoryblokPage.currentStory,
      fetchingInProgress: state.StoryblokPage.fetchingInProgress,
      error: state.StoryblokPage.error,
    }),
    shallowEqual,
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useAppTheme();

  if (
    typeof window !== 'undefined' &&
    process.env.REACT_APP_ENV === 'development' &&
    currentStory
  ) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useStoryblokBridge(currentStory.id, (story) =>
      dispatch(bridgeUpdateStoryblokStory(story, new URLSearchParams(location.search), intl.locale)),
    );
  }

  if ((!currentStory || fetchingInProgress) && !error) {
    return <StoryblokLoadingPage />;
  }

  if ((!currentStory && !error) || (error && error.status === 404)) {
    return <NamedRedirect name="NotFoundPage" />;
  }

  if (error) {
    return <StoryblokErrorPage />;
  }

  const { name: pageName, content, hasHeyFlow } = currentStory;
  const { metatags } = content;
  const keywords = content.keywords ? content.keywords.split(',').map((kw) => kw.trim()) : [];
  const { title = '', description = '' } = metatags || {};

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
  };

  // hreflang alternates: the current locale (self) plus every PUBLISHED translation,
  // each using its own translated slug. Drafts are omitted — those URLs 404 by design.
  const alternateLinks = localizedAlternateLinks({
    translatedSlugs: currentStory.translated_slugs,
    currentLocale: intl.locale,
    selfHref: `${config.canonicalRootURL}${location.pathname}`,
    canonicalRootURL: config.canonicalRootURL,
  });

  const linkImage = currentStory?.content?.linkImage?.filename;
  const facebookImages = linkImage
    ? [
      {
        name: 'facebook',
        url: linkImage,
        width: 1200,
        height: 630,
      },
    ]
    : undefined;

  const twitterImages = linkImage
    ? [
      {
        name: 'twitter',
        url: linkImage,
        width: 600,
        height: 314,
      },
    ]
    : undefined;

  return (
    <Page
      customScripts={[
        ...(hasHeyFlow ? ['https://static.heyflow.com/widget/latest/webview.js'] : []),
      ]}
      description={description}
      {...(facebookImages ? { facebookImages } : {})}
      {...(twitterImages ? { twitterImages } : {})}
      alternateLinks={alternateLinks}
      keywords={keywords}
      schema={schema}
      title={title}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopBar currentPage="StoryblokPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <Box
            sx={{
              minHeight: `calc(100vh - ${theme.globals.topBarHeightDesktopTotal}px)`,
            }}
          >
            <StoryblokComponent blok={content} story={currentStory} />
          </Box>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
        <LayoutWrapperBottombar>
          <BottomNavigationBar />
        </LayoutWrapperBottombar>
      </LayoutSingleColumn>
    </Page>
  );
};

export default StoryblokPage;
